/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

/**
 * Model tier system for cost/quality filtering
 * Allows users to choose models by price tier and origin
 */

export type ModelTier = 'ultra-cheap' | 'budget' | 'premium-cheap' | 'premium-expensive'
export type ModelOrigin = 'us' | 'eu' | 'china' | 'international'

export interface ModelMetadata {
  id: string
  name: string
  provider: string
  tier: ModelTier
  origin: ModelOrigin
  costPerMTokens: {
    input: number
    output: number
  }
  contextWindow: number
  strengths: string[]
  recommended?: boolean
  directApiSupported?: boolean // Whether we have direct API integration
}

/**
 * Comprehensive model catalog with tier and origin metadata
 */
export const MODEL_CATALOG: ModelMetadata[] = [
  // ULTRA-CHEAP TIER ($0.01-$0.20 per M tokens)
  {
    id: 'deepseek/deepseek-chat',
    name: 'DeepSeek Chat V3',
    provider: 'DeepSeek',
    tier: 'ultra-cheap',
    origin: 'china',
    costPerMTokens: { input: 0.014, output: 0.028 },
    contextWindow: 64000,
    strengths: ['reasoning', 'programming', 'research', 'cost-effective'],
    recommended: true,
    directApiSupported: true
  },
  {
    id: 'deepseek/deepseek-coder',
    name: 'DeepSeek Coder',
    provider: 'DeepSeek',
    tier: 'ultra-cheap',
    origin: 'china',
    costPerMTokens: { input: 0.014, output: 0.028 },
    contextWindow: 64000,
    strengths: ['programming', 'code-generation', 'debugging'],
    recommended: true,
    directApiSupported: true
  },
  {
    id: 'groq/llama-3.3-70b-versatile',
    name: 'Llama 3.3 70B (Groq)',
    provider: 'Groq',
    tier: 'ultra-cheap',
    origin: 'us',
    costPerMTokens: { input: 0.20, output: 0.20 },
    contextWindow: 128000,
    strengths: ['speed', 'reasoning', 'multilingual', 'us-based'],
    recommended: true,
    directApiSupported: true
  },
  {
    id: 'groq/mixtral-8x7b-32768',
    name: 'Mixtral 8x7B (Groq)',
    provider: 'Groq',
    tier: 'ultra-cheap',
    origin: 'eu',
    costPerMTokens: { input: 0.20, output: 0.20 },
    contextWindow: 32768,
    strengths: ['speed', 'multilingual', 'eu-based'],
    directApiSupported: true
  },
  {
    id: 'together/meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo',
    name: 'Llama 3.1 70B Turbo (Together)',
    provider: 'Together AI',
    tier: 'ultra-cheap',
    origin: 'us',
    costPerMTokens: { input: 0.18, output: 0.18 },
    contextWindow: 131072,
    strengths: ['open-source', 'reasoning', 'us-based'],
    recommended: true,
    directApiSupported: true
  },
  {
    id: 'together/meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo',
    name: 'Llama 3.1 8B Turbo (Together)',
    provider: 'Together AI',
    tier: 'ultra-cheap',
    origin: 'us',
    costPerMTokens: { input: 0.06, output: 0.06 },
    contextWindow: 131072,
    strengths: ['ultra-cheap', 'fast', 'us-based'],
    recommended: true,
    directApiSupported: true
  },
  {
    id: 'siliconflow/Qwen/Qwen2.5-72B-Instruct',
    name: 'Qwen 2.5 72B (SiliconFlow)',
    provider: 'SiliconFlow',
    tier: 'ultra-cheap',
    origin: 'china',
    costPerMTokens: { input: 0.086, output: 0.086 },
    contextWindow: 32768,
    strengths: ['multilingual', 'reasoning', 'cost-effective'],
    directApiSupported: true
  },
  {
    id: 'siliconflow/Qwen/Qwen2.5-7B-Instruct',
    name: 'Qwen 2.5 7B (SiliconFlow)',
    provider: 'SiliconFlow',
    tier: 'ultra-cheap',
    origin: 'china',
    costPerMTokens: { input: 0.05, output: 0.05 },
    contextWindow: 32768,
    strengths: ['ultra-cheap', 'multilingual', 'fast'],
    recommended: true,
    directApiSupported: true
  },
  {
    id: 'qwen/qwen3-235b-a22b',
    name: 'Qwen 3 235B-A22B (MoE)',
    provider: 'Qwen',
    tier: 'ultra-cheap',
    origin: 'china',
    costPerMTokens: { input: 0.10, output: 0.10 },
    contextWindow: 128000,
    strengths: ['reasoning', 'thinking-mode', 'coding', 'multilingual', '119-languages'],
    recommended: true,
    directApiSupported: false
  },
  {
    id: 'qwen/qwen3-30b-a3b',
    name: 'Qwen 3 30B-A3B (MoE)',
    provider: 'Qwen',
    tier: 'ultra-cheap',
    origin: 'china',
    costPerMTokens: { input: 0.08, output: 0.08 },
    contextWindow: 128000,
    strengths: ['reasoning', 'thinking-mode', 'coding', 'ultra-cheap', '119-languages'],
    recommended: true,
    directApiSupported: false
  },
  {
    id: 'qwen/qwen3-32b',
    name: 'Qwen 3 32B',
    provider: 'Qwen',
    tier: 'ultra-cheap',
    origin: 'china',
    costPerMTokens: { input: 0.09, output: 0.09 },
    contextWindow: 128000,
    strengths: ['reasoning', 'coding', 'multilingual', '119-languages'],
    directApiSupported: false
  },
  {
    id: 'qwen/qwen3-14b',
    name: 'Qwen 3 14B',
    provider: 'Qwen',
    tier: 'ultra-cheap',
    origin: 'china',
    costPerMTokens: { input: 0.07, output: 0.07 },
    contextWindow: 128000,
    strengths: ['reasoning', 'coding', 'multilingual', '119-languages'],
    directApiSupported: false
  },
  {
    id: 'qwen/qwen3-8b',
    name: 'Qwen 3 8B',
    provider: 'Qwen',
    tier: 'ultra-cheap',
    origin: 'china',
    costPerMTokens: { input: 0.06, output: 0.06 },
    contextWindow: 128000,
    strengths: ['reasoning', 'coding', 'fast', '119-languages'],
    directApiSupported: false
  },

  // BUDGET TIER ($0.25-$1.00 per M tokens)
  {
    id: 'openai/gpt-4o-mini',
    name: 'GPT-4o Mini',
    provider: 'OpenAI',
    tier: 'budget',
    origin: 'us',
    costPerMTokens: { input: 0.15, output: 0.60 },
    contextWindow: 128000,
    strengths: ['fast', 'multimodal', 'us-based', 'reliable'],
    recommended: true,
    directApiSupported: true
  },
  {
    id: 'google/gemini-2.0-flash-exp',
    name: 'Gemini 2.0 Flash',
    provider: 'Google',
    tier: 'budget',
    origin: 'us',
    costPerMTokens: { input: 0.30, output: 1.20 },
    contextWindow: 1000000,
    strengths: ['huge-context', 'multimodal', 'us-based', 'fast'],
    recommended: true,
    directApiSupported: true
  },
  {
    id: 'anthropic/claude-3-haiku-20240307',
    name: 'Claude 3 Haiku',
    provider: 'Anthropic',
    tier: 'budget',
    origin: 'us',
    costPerMTokens: { input: 0.80, output: 4.00 },
    contextWindow: 200000,
    strengths: ['fast', 'reasoning', 'us-based', 'reliable'],
    recommended: true,
    directApiSupported: true
  },

  // PREMIUM-CHEAP TIER ($1.00-$5.00 per M tokens)
  {
    id: 'openai/gpt-4o',
    name: 'GPT-4o',
    provider: 'OpenAI',
    tier: 'premium-cheap',
    origin: 'us',
    costPerMTokens: { input: 2.50, output: 10.00 },
    contextWindow: 128000,
    strengths: ['multimodal', 'reasoning', 'us-based', 'high-quality'],
    recommended: true,
    directApiSupported: true
  },
  {
    id: 'google/gemini-2.5-pro',
    name: 'Gemini 2.5 Pro',
    provider: 'Google',
    tier: 'premium-cheap',
    origin: 'us',
    costPerMTokens: { input: 1.25, output: 5.00 },
    contextWindow: 2000000,
    strengths: ['huge-context', 'multimodal', 'us-based', 'reasoning'],
    recommended: true,
    directApiSupported: true
  },
  {
    id: 'google/gemini-2.5-flash',
    name: 'Gemini 2.5 Flash',
    provider: 'Google',
    tier: 'budget',
    origin: 'us',
    costPerMTokens: { input: 0.075, output: 0.30 },
    contextWindow: 1000000,
    strengths: ['fast', 'affordable', 'multimodal', 'us-based'],
    recommended: true,
    directApiSupported: true
  },
  {
    id: 'google/gemini-3-deep-think',
    name: 'Gemini 3 Deep Think (Experimental)',
    provider: 'Google',
    tier: 'premium-expensive',
    origin: 'us',
    costPerMTokens: { input: 2.00, output: 12.00 },
    contextWindow: 1000000,
    strengths: ['maximum-reasoning', 'complex-coding', 'problem-formulation', 'tradeoff-analysis', 'experimental'],
    recommended: false,
    directApiSupported: true
  },
  {
    id: 'anthropic/claude-3-5-sonnet-20241022',
    name: 'Claude 3.5 Sonnet',
    provider: 'Anthropic',
    tier: 'premium-cheap',
    origin: 'us',
    costPerMTokens: { input: 3.00, output: 15.00 },
    contextWindow: 200000,
    strengths: ['reasoning', 'coding', 'us-based', 'high-quality'],
    recommended: true,
    directApiSupported: true
  },

  // PREMIUM-EXPENSIVE TIER ($5.00+ per M tokens) - Best in class
  {
    id: 'anthropic/claude-sonnet-4.5',
    name: 'Claude Sonnet 4.5',
    provider: 'Anthropic',
    tier: 'premium-expensive',
    origin: 'us',
    costPerMTokens: { input: 3.00, output: 15.00 },
    contextWindow: 200000,
    strengths: ['best-reasoning', 'coding', 'us-based', 'frontier'],
    recommended: true,
    directApiSupported: true
  },
  {
    id: 'openai/gpt-5',
    name: 'GPT-5',
    provider: 'OpenAI',
    tier: 'premium-expensive',
    origin: 'us',
    costPerMTokens: { input: 5.00, output: 15.00 },
    contextWindow: 128000,
    strengths: ['frontier', 'reasoning', 'us-based', 'best-in-class'],
    recommended: true,
    directApiSupported: true
  },
  {
    id: 'anthropic/claude-3-opus-20240229',
    name: 'Claude 3 Opus',
    provider: 'Anthropic',
    tier: 'premium-expensive',
    origin: 'us',
    costPerMTokens: { input: 15.00, output: 75.00 },
    contextWindow: 200000,
    strengths: ['best-reasoning', 'complex-tasks', 'us-based', 'frontier'],
    directApiSupported: true
  },
  {
    id: 'x-ai/grok-4',
    name: 'Grok 4',
    provider: 'xAI',
    tier: 'premium-expensive',
    origin: 'us',
    costPerMTokens: { input: 5.00, output: 15.00 },
    contextWindow: 131072,
    strengths: ['real-time-data', 'reasoning', 'us-based'],
    directApiSupported: false
  },
]

/**
 * Filter models by tier
 */
export function getModelsByTier(tier: ModelTier): ModelMetadata[] {
  return MODEL_CATALOG.filter(m => m.tier === tier)
}

/**
 * Filter models by origin (for users who prefer US/EU models)
 */
export function getModelsByOrigin(origin: ModelOrigin): ModelMetadata[] {
  return MODEL_CATALOG.filter(m => m.origin === origin)
}

/**
 * Get models by tier and origin
 */
export function getModelsByTierAndOrigin(tier: ModelTier, origin?: ModelOrigin): ModelMetadata[] {
  let models = getModelsByTier(tier)
  if (origin) {
    models = models.filter(m => m.origin === origin)
  }
  return models
}

/**
 * Get recommended models for a tier
 */
export function getRecommendedModelsByTier(tier: ModelTier): ModelMetadata[] {
  return MODEL_CATALOG.filter(m => m.tier === tier && m.recommended)
}

/**
 * Get cheapest model overall
 */
export function getCheapestModel(): ModelMetadata {
  return MODEL_CATALOG.reduce((cheapest, current) => {
    const cheapestAvg = (cheapest.costPerMTokens.input + cheapest.costPerMTokens.output) / 2
    const currentAvg = (current.costPerMTokens.input + current.costPerMTokens.output) / 2
    return currentAvg < cheapestAvg ? current : cheapest
  })
}

/**
 * Get cheapest US-based model
 */
export function getCheapestUSModel(): ModelMetadata {
  const usModels = MODEL_CATALOG.filter(m => m.origin === 'us')
  return usModels.reduce((cheapest, current) => {
    const cheapestAvg = (cheapest.costPerMTokens.input + cheapest.costPerMTokens.output) / 2
    const currentAvg = (current.costPerMTokens.input + current.costPerMTokens.output) / 2
    return currentAvg < cheapestAvg ? current : cheapest
  })
}

/**
 * Get best model in tier (by recommendation and quality)
 */
export function getBestModelInTier(tier: ModelTier): ModelMetadata {
  const tierModels = getModelsByTier(tier)
  const recommended = tierModels.filter(m => m.recommended)
  return recommended.length > 0 ? recommended[0] : tierModels[0]
}

/**
 * Provider-specific presets
 */
export interface ProviderPreset {
  id: string
  name: string
  provider: string
  description: string
  freeApiAvailable: boolean
  freeApiUrl?: string
  rateLimits?: {
    requestsPerMinute: number
    requestsPerDay: number
    tier: 'free' | 'paid'
  }
  models: {
    orchestrator: string
    judge: string
    peers: string[]
    ragSearch: string
    documentEditor: string
    toolCalling: string
  }
}

export const PROVIDER_PRESETS: ProviderPreset[] = [
  {
    id: 'google-free',
    name: 'Google AI Studio (Free)',
    provider: 'Google',
    description: 'Use only Google Gemini models with free API from Google AI Studio. Perfect for getting started!',
    freeApiAvailable: true,
    freeApiUrl: 'https://aistudio.google.com',
    rateLimits: {
      requestsPerMinute: 15,
      requestsPerDay: 1500,
      tier: 'free'
    },
    models: {
      orchestrator: 'google/gemini-2.5-pro',
      judge: 'google/gemini-2.5-pro',
      peers: [
        'google/gemini-2.5-pro',
        'google/gemini-2.5-flash',
        'google/gemini-2.0-flash-exp'
      ],
      ragSearch: 'google/gemini-2.0-flash-exp',
      documentEditor: 'google/gemini-2.5-pro',
      toolCalling: 'google/gemini-2.5-pro'
    }
  }
]

/**
 * Get preset by ID
 */
export function getProviderPreset(id: string): ProviderPreset | undefined {
  return PROVIDER_PRESETS.find(p => p.id === id)
}

/**
 * Get all Google models
 */
export function getGoogleModels(): ModelMetadata[] {
  return MODEL_CATALOG.filter(m => m.provider === 'Google')
}

/**
 * Tier descriptions for UI
 */
export const TIER_DESCRIPTIONS: Record<ModelTier, { name: string; description: string; priceRange: string }> = {
  'ultra-cheap': {
    name: 'Ultra-Cheap',
    description: 'Best value. 10-100x cheaper than premium models. Great for most tasks.',
    priceRange: '$0.01-$0.20 per M tokens'
  },
  'budget': {
    name: 'Budget',
    description: 'Balanced cost and quality. Good for everyday use.',
    priceRange: '$0.25-$1.00 per M tokens'
  },
  'premium-cheap': {
    name: 'Premium (Affordable)',
    description: 'High quality at reasonable cost. Best for important work.',
    priceRange: '$1.00-$5.00 per M tokens'
  },
  'premium-expensive': {
    name: 'Premium (Best-in-Class)',
    description: 'Frontier models. Maximum capability for critical tasks.',
    priceRange: '$5.00+ per M tokens'
  }
}


